import { getResponse } from '@satellite/services/serviceUtilities';
import { cacheableGet } from '@satellite/services/httpClient';

export default {
  /**
   * Uploads a file against a related entity (e.g. `YardTask`) and returns the created
   * document. The response carries the authenticated `url` of the download endpoint.
   *
   * @param {string} relatedEntity - The related entity type (a `DatabaseEntities` value)
   * @param {string} relatedId - The related entity ID
   * @param {File} file - The file to upload
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (signal, onUploadProgress, etc.)
   * @returns {Promise<{ id: string, name: string, mimetype: string, url: string }>} The created document
   */
  async uploadDocument(
    relatedEntity,
    relatedId,
    file,
    { getResponseConfig = {}, requestConfig = {} } = {}
  ) {
    const formData = new FormData();
    formData.append('file', file);

    // Content-Type is intentionally left unset so the browser adds the multipart boundary.
    const response = await axios.post(
      `/document/${relatedEntity}/${relatedId}`,
      formData,
      requestConfig
    );

    return getResponse(response, getResponseConfig);
  },

  /**
   * Lists the documents attached to a related entity (e.g. `YardTask`). Each returned document
   * carries the authenticated download endpoint as its `url`, or a presigned URL valid for
   * `expirationInSeconds` that the browser can fetch directly when `presignedUrls` is set.
   *
   * @param {Object} query - Query params
   * @param {string} query.relatedEntity - The related entity type (a `DatabaseEntities` value)
   * @param {string} query.relatedId - The related entity ID
   * @param {boolean} [query.presignedUrls] - Return presigned S3 URLs instead of the download endpoint
   * @param {number} [query.expirationInSeconds] - Lifetime of the presigned URLs (defaults to one hour server-side)
   * @param {number} [query.limit] - Page size
   * @param {number} [query.offset] - Page offset
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching (default: true; disabled automatically when signal is provided)
   * @returns {Promise<{
   *   items: Array<{ id: string, name: string, mimetype: string, url: string }>,
   *   meta: {
   *     totalRecords: number,
   *     totalPages: number,
   *     page: number,
   *     limit: number,
   *     offset: number
   *   }
   * }>} The documents page
   */
  async listDocuments(
    { relatedEntity, relatedId, presignedUrls, expirationInSeconds, limit, offset },
    { getResponseConfig = {}, requestConfig = {}, enableCache = true } = {}
  ) {
    const response = await cacheableGet('/document', {
      ...requestConfig,
      params: {
        relatedEntity,
        relatedId,
        presignedUrls,
        expirationInSeconds,
        limit,
        offset,
        ...requestConfig.params
      },
      enableCache
    });

    return getResponse(response, getResponseConfig);
  },

  /**
   * Removes a document: it stops being listed and its URL stops working. Cannot be undone.
   *
   * Return type matches `getResponse`: unwrapped response data by default, or the full
   * axios response when `getResponseConfig.includeMetaData` is true.
   *
   * @overload
   * @param {string} documentId - The ID of the document to remove
   * @param {{
   *   getResponseConfig: { includeMetaData: true },
   *   requestConfig?: import('axios').AxiosRequestConfig
   * }} options - Request options with metadata included
   * @returns {Promise<import('axios').AxiosResponse>} Full axios response from `getResponse`
   *
   * @overload
   * @param {string} documentId - The ID of the document to remove
   * @param {{
   *   getResponseConfig?: { includeMetaData?: false | null },
   *   requestConfig?: import('axios').AxiosRequestConfig
   * }} [options] - Request options (default: unwrap response data)
   * @returns {Promise<*>} Unwrapped response data from `getResponse`
   *
   * @param {string} documentId - The ID of the document to remove
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (signal, headers, etc.)
   * @returns {Promise<* | import('axios').AxiosResponse>}
   */
  async deleteDocument(documentId, { getResponseConfig = {}, requestConfig = {} } = {}) {
    const response = await axios.delete(`/document/${documentId}`, requestConfig);

    return getResponse(response, getResponseConfig);
  },

  /**
   * Fetches a document from the authenticated neutron API and returns a
   * temporary object URL that can be opened in a new tab. The caller is
   * responsible for calling URL.revokeObjectURL() when the URL is no longer
   * needed.
   *
   * @param {string} documentId
   * @returns {Promise<string>} A blob object URL
   */
  async getDownloadBlobUrl(documentId) {
    const response = await axios.get(`/document/${documentId}/download`, {
      responseType: 'blob'
    });
    return URL.createObjectURL(response.data);
  }
};
